import { Provider, Type } from '@angular/core';
import { TestBed, waitForAsync } from '@angular/core/testing';
import { BrowserDynamicTestingModule } from '@angular/platform-browser-dynamic/testing';

import { BaseSpecOptions, BaseSpecOverrides } from '../base/options';
import { setProps } from '../internals/query';
import * as customMatchers from '../matchers';
import { addMatchers } from '../core';
import { isType } from '../types';

import { initialSpecModule } from './initial-module';
import { getSpecDefaultOptions, SpecOptions } from './options';
import { Spec } from './Spec';

/**
 * @publicApi
 */
export type SpecFactory<C> = (options?: SpecOverrides<C>) => Spec<C>;

/**
 * @publicApi
 */
export interface SpecOverrides<C> extends BaseSpecOverrides {
  detectChanges?: boolean;
  props?: Partial<C>;
}

/**
 * @internal
 */
export function overrideComponentIfProviderOverridesSpecified<C>(options: Required<SpecOptions<C>>): void {
  const hasProviderOverrides = options.componentProviders.length || options.componentMocks.length;
  const hasViewProviders = options.componentViewProviders.length || options.componentViewProvidersMocks.length;
  if (hasProviderOverrides || hasViewProviders) {
    let providerConfiguration = {};
    if (hasProviderOverrides) {
      providerConfiguration = {
        providers: [...options.componentProviders, ...options.componentMocks.map(p => options.mockProvider(p))]
      };
    }
    if (hasViewProviders) {
      providerConfiguration = {
        ...providerConfiguration,
        viewProviders: [...options.componentViewProviders, ...options.componentViewProvidersMocks.map(p => options.mockProvider(p))]
      };
    }
    TestBed.overrideComponent(options.component, {
      set: providerConfiguration
    });
  }
}

/**
 * @internal
 */
export function overrideModules(options: Required<BaseSpecOptions>): void {
  if (options.overrideModules.length) {
    options.overrideModules.forEach(overrideModule => {
      const [ngModule, override] = overrideModule;

      TestBed.overrideModule(ngModule, override);
    });
  }
}

/**
 * @publicApi
 */
export function createComponentFactory<C>(typeOrOptions: Type<C> | SpecOptions<C>): SpecFactory<C> {
  const options = isType(typeOrOptions)
    ? getSpecDefaultOptions<C>({ component: typeOrOptions })
    : getSpecDefaultOptions(typeOrOptions);

  const moduleMetadata = initialSpecModule<C>(options);

  beforeEach(
    waitForAsync(() => {
      addMatchers(customMatchers);
      TestBed.configureTestingModule(moduleMetadata).overrideModule(BrowserDynamicTestingModule, {
        set: {
          entryComponents: moduleMetadata.entryComponents
        }
      });

      overrideModules(options);

      overrideComponentIfProviderOverridesSpecified(options);

      TestBed.compileComponents();
    })
  );

  return (overrides?: SpecOverrides<C>) => {
    const defaults: SpecOverrides<C> = { props: {}, detectChanges: true, providers: [] };
    const { detectChanges, props, providers } = { ...defaults, ...overrides };

    if (providers && providers.length) {
      providers.forEach((provider: Provider) => {
        TestBed.overrideProvider((provider as any).provide, provider as any);
      });
    }

    const Spec = createSpec(options, props);

    if (options.detectChanges && detectChanges) {
      Spec.detectChanges();
    }

    return Spec;
  };
}

function createSpec<C>(options: Required<SpecOptions<C>>, props?: Partial<C>): Spec<C> {
  const fixture = TestBed.createComponent(options.component);
  const debugElement = fixture.debugElement;

  const component = setProps(fixture.componentInstance, props);

  return new Spec(fixture, debugElement, component, debugElement.nativeElement);
}
